'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/** Hairline con ornamento ✦ centrado — detalle "fine dining" del tema dark. */
function Rule({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center gap-3 ${className}`} aria-hidden>
      <span
        className="block h-px w-12 sm:w-20"
        style={{ background: 'linear-gradient(to right, transparent, color-mix(in srgb, var(--ms-accent) 72%, transparent))' }}
      />
      <span className="text-[10px] leading-none" style={{ color: 'var(--ms-accent)' }}>✦</span>
      <span
        className="block h-px w-12 sm:w-20"
        style={{ background: 'linear-gradient(to left, transparent, color-mix(in srgb, var(--ms-accent) 72%, transparent))' }}
      />
    </div>
  )
}

/**
 * Hero del tema "dark" (Nocturno luxe): fine-dining de noche. Composición
 * centrada y simétrica sobre negro CÁLIDO, con hairlines del color de acento,
 * un ornamento ✦, viñeta para profundidad y serif de alto contraste (Cormorant).
 * Si hay foto/video va a sangre bajo un overlay muy oscuro; si no, el fondo es
 * negro con un glow de acento tenue. Lee tokens del tema (--ms-*).
 */
export function HeroDark({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="relative w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* Media a sangre (opcional) */}
      {heroVideoUrl ? (
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
      ) : heroImageUrl ? (
        <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
      ) : null}

      {/* Overlay oscuro (token) sobre la media para legibilidad */}
      {hasMedia && <div className="absolute inset-0 z-10" style={{ background: 'var(--ms-hero-overlay)' }} />}

      {/* Atmósfera: glow de acento tenue arriba + viñeta para dar profundidad */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'radial-gradient(120% 78% at 50% -12%, color-mix(in srgb, var(--ms-accent) 15%, transparent), transparent 56%)' }}
      />
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'radial-gradient(78% 62% at 50% 46%, transparent 48%, rgba(0,0,0,0.62) 100%)' }}
      />

      {/* Contenido centrado */}
      <div
        className="relative z-20 mx-auto flex max-w-3xl flex-col items-center justify-center px-6 text-center"
        style={{ minHeight: 'min(680px, 88vh)' }}
      >
        <Rule />

        {city && (
          <span className="mt-6 text-[11px] font-semibold uppercase tracking-[0.42em] pl-[0.42em]" style={{ color: 'var(--ms-accent)' }}>
            {city}
          </span>
        )}

        {/* El nombre en serif protagoniza (Cormorant). No usamos el logo aquí:
            muchos logos tienen tinta oscura que desaparece sobre el negro. */}
        <h1
          className="mt-5 font-medium leading-[1.0] tracking-[0.005em] text-[clamp(46px,9vw,94px)]"
          style={{
            fontFamily: 'var(--ms-font-display)',
            color: 'var(--ms-text)',
            textShadow: hasMedia ? '0 4px 30px rgba(0,0,0,0.6)' : undefined,
          }}
        >
          {businessName}
        </h1>

        {tagline && (
          <p
            className="mt-4 max-w-xl text-[17px] sm:text-[20px] italic leading-snug"
            style={{ fontFamily: 'var(--ms-font-display)', color: 'var(--ms-text-soft)' }}
          >
            {tagline}
          </p>
        )}

        <Rule className="mt-7" />

        {/* Meta discreta */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12.5px] tracking-wide" style={{ color: 'var(--ms-text-muted)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-2">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#5fcf80'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : '#8a8175',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs */}
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center gap-2 px-9 py-4 text-[13px] font-bold uppercase tracking-[0.16em] transition hover:brightness-105 active:scale-[0.98]"
            style={{
              background: 'var(--ms-text)',
              color: 'var(--ms-bg)',
              borderRadius: 'var(--ms-radius)',
              boxShadow: '0 18px 42px -18px rgba(0,0,0,0.75)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center px-7 py-4 text-[12.5px] font-semibold uppercase tracking-[0.16em] transition hover:bg-white/5"
              style={{
                color: 'var(--ms-text)',
                borderRadius: 'var(--ms-radius)',
                border: '1px solid color-mix(in srgb, var(--ms-accent) 45%, transparent)',
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
